import { Error, Label } from '@progress/kendo-react-labels';
import {
  TiloLanguagesAll,
  TiloLanguagesByDoors
} from '../constants/tilo-languages';
import {
  ComboBox,
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import { Checkbox, TextArea } from '@progress/kendo-react-inputs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { ComboboxInput, FormInput, OptimizedComboboxInput } from './form-input';
import { User } from '../types/user';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';

import {
  EventPlaylist,
  EventPlaylistFacilitator,
  EventPlaylistLocation,
  Challenge
} from '../types/event';
import { Library } from '../types/library';
import { Autocomplete } from './auto-complete';
import { EnvironmentList } from '../types/environment';
import { convertJsonDateToDate, getTotalMinutes } from '../utils/date';
import { CountryResponse } from '../types/responses/country-response';
import { TimeZoneResponse } from '../types/responses/timezone-response';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Assessment } from '../types/assessment';
import YesNoDialog from './dialogs/yes-no-dialog';
import { buildDefaultTilConfig } from '../constants/til-map-data';
import { YesNoDialogResult } from '../types/dialog-result';
import { useSearchParams } from 'react-router-dom';
import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon } from '@progress/kendo-svg-icons';
import cloneDeep from 'lodash.clonedeep';
import { useToast } from '../contexts/toast-context';
import { QRLink } from './QRLink';

export interface GameEventProps {
  users?: User[];
  index?: number;
  game?: EventPlaylist;
  languages?: string[];
  libraries?: Library[];
  assessments?: Assessment[];
  countries?: CountryResponse[];
  timezones?: TimeZoneResponse[];
  environments?: EnvironmentList[];
  locations?: EventPlaylistLocation[];
  facilitators?: EventPlaylistFacilitator[];
  onCopyGame?: (index: number) => void;
  onDeleteGame?: (index: number) => void;
  onChangeGameSettings?: (gameId: number) => void;
  onChange?: (game: EventPlaylist, index: number) => void;
  onChangeLocation: (locations: EventPlaylistLocation[]) => void;
  onChangeFacilitator: (facilitator: EventPlaylistFacilitator) => void;
  challenges?: Challenge[];
}

export type dictionary = {
  id: number;
  name: string;
};

const getLocalTimeZoneId = (
  timezones: TimeZoneResponse[]
): string | undefined => {
  if (!timezones || timezones.length === 0) return undefined;

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 1. Direct match with id
  const directMatch = timezones.find(
    (tz) => tz.id?.toLowerCase() === browserTimeZone.toLowerCase()
  );
  if (directMatch?.id) return directMatch.id;

  // 2. Match standardName or daylightName
  const nameMatch = timezones.find(
    (tz) =>
      tz.standardName?.toLowerCase() === browserTimeZone.toLowerCase() ||
      tz.daylightName?.toLowerCase() === browserTimeZone.toLowerCase()
  );
  if (nameMatch?.id) return nameMatch.id;

  // 3. Match by offset and sign
  const offsetMinutes = -new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const browserOffsetStr = `${String(offsetHours).padStart(2, '0')}:${String(
    offsetMins
  ).padStart(2, '0')}`;

  const offsetMatch = timezones.find((tz) => {
    if (!tz.baseUtcOffset) return false;
    // Check if the sign is correct
    const hasPlus =
      tz.baseUtcOffset.includes('+') || !tz.baseUtcOffset.includes('-');
    const tzSign = hasPlus ? '+' : '-';
    if (tzSign !== sign) return false;

    // Check if offset values match (e.g., "07:00")
    return tz.baseUtcOffset.includes(browserOffsetStr);
  });

  return offsetMatch?.id;
};

export const ActivityEvent = ({
  game,
  index,
  users,
  locations,
  libraries,
  countries,
  languages,
  timezones,
  assessments,
  facilitators,
  environments,
  onChange,
  onCopyGame,
  onDeleteGame,
  onChangeLocation = () => {},
  onChangeFacilitator = () => {},
  onChangeGameSettings = () => {},
  challenges = []
}: GameEventProps) => {
  const [searchParams] = useSearchParams();
  const { onToastOpen } = useToast();
  const [eventGame, setEventGame] = useState<EventPlaylist>({});
  const [hasAutoSetTz, setHasAutoSetTz] = useState(false);
  const isWst =
    game?.type === 'WST' || game?.name === "World's Strongest Team";
  const tiloLanguages = eventGame.tiloNbrDoors
    ? (TiloLanguagesByDoors[eventGame.tiloNbrDoors as 8 | 12] ??
      TiloLanguagesAll)
    : TiloLanguagesAll;
  const [facilitatorsState, setFacilitatorsState] = useState<
    EventPlaylistFacilitator[]
  >([]);
  const [locationsState, setLocationsState] = useState<EventPlaylistLocation[]>(
    locations || []
  );

  const [gameData, setGameData] = useState<Library[]>(libraries ?? []);
  const [assessmentData, setAssessmentData] = useState<Assessment[]>(
    assessments ?? []
  );

  useEffect(() => {
    setGameData(libraries ?? []);
  }, [libraries]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  // Reset language when nbrDoors changes and the selected language is no longer valid
  useEffect(() => {
    if (
      eventGame.type === 'TILO' &&
      eventGame.language &&
      !tiloLanguages.includes(eventGame.language)
    ) {
      onChangeField('language', '');
    }
  }, [eventGame.tiloNbrDoors]);

  useEffect(() => {
    onChangeLocation(locationsState);
  }, [locationsState]);

  useEffect(() => {
    if (facilitators) {
      setFacilitatorsState(facilitators);
    }
  }, [facilitators]);

  const validEditing = (showToast: boolean = true) => {
    const actionParams = searchParams.get('action');
    let totalMinute = getTotalMinutes(
      new Date(game?.startDateUtc!),
      new Date()
    );
    if (actionParams !== 'copy' && game?.id! > 0 && totalMinute > 0) {
      if (showToast) {
        onToastOpen({
          style: 'error',
          title: 'Error',
          body: (
            <div>{`Changes to an activity that has started are not permitted.`}</div>
          )
        });
      }

      return false;
    }

    return true;
  };

  const onDeleteFacilitator = (facilitator: EventPlaylistFacilitator) => {
    if (!validEditing()) {
      return;
    }

    if (facilitatorsState) {
      const idx = facilitatorsState.indexOf(facilitator);
      if (idx > -1) {
        facilitatorsState[idx].isDeleted = true;
        onChangeFacilitator(facilitatorsState[idx]);
      }
    }
  };

  const onDeleteLocation = (
    location: EventPlaylistLocation,
    action: 'delete' | 'reset'
  ) => {
    if (!validEditing()) {
      return;
    }

    const idx = locationsState.indexOf(location);
    if (idx > -1) {
      setLocationsState((prev) => {
        const result = cloneDeep(prev);
        action === 'delete'
          ? result.splice(idx, 1)
          : result.splice(idx, 1, {
              id: 0,
              eventPlaylistId: 0,
              locationCountryISO: '',
              country: '',
              city: '',
              isDeleted: false
            });

        return result;
      });
    }
  };

  const handleDeleteGame = () => {
    if (!validEditing()) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes' && onDeleteGame && index !== undefined) {
      try {
        await onDeleteGame(index);
        onToastOpen({
          style: 'success',
          title: 'My events',
          body: <div>Activity deleted.</div>
        });
      } catch {
        onToastOpen({
          style: 'error',
          title: 'My events',
          body: <div>Failed to delete activity.</div>
        });
      }
    }
    setShowDeleteConfirm(false);
  };

  const handleChangeGameSettings = () => {
    onChangeGameSettings(game?.gameId!);
  };

  const handleCopyGame = () => {
    if (onCopyGame && index !== undefined) {
      onCopyGame(index);
    }
  };

  const onChangeActivityHandler = (game: EventPlaylist) => {
    if (!validEditing()) {
      return;
    }
    if (onChange && (index as number) > -1) {
      onChange(game, index as number);
    }
  };

  const onChangeField = useCallback(
    <K extends keyof EventPlaylist>(field: K, value: EventPlaylist[K]) => {
      if (!validEditing()) {
        return;
      }

      const newEvent = { ...game };

      if (newEvent && field) {
        newEvent[field] = value;
      }

      if (newEvent) {
        onChangeActivityHandler(newEvent);
      }
    },
    [game, validEditing, onChangeActivityHandler]
  );

  const onChangeDateField = (
    field: keyof EventPlaylist,
    value: Date | null
  ) => {
    if (!validEditing()) {
      return;
    }
    if (value) {
      let newEvent = { ...game };

      newEvent[field] = value as any;

      if (newEvent) {
        onChangeActivityHandler(newEvent);
      }
    }
  };

  useEffect(() => {
    if (
      timezones &&
      timezones.length > 0 &&
      game &&
      !game.timeZone &&
      !(game as any).timezone &&
      !hasAutoSetTz &&
      validEditing(false)
    ) {
      const detectedTzId = getLocalTimeZoneId(timezones);
      if (detectedTzId) {
        setHasAutoSetTz(true);
        onChangeField('timeZone', detectedTzId);
      }
    }
  }, [timezones, game?.timeZone, (game as any)?.timezone, hasAutoSetTz]);

  const onChangeLocationHandler = (idx: number, country: CountryResponse) => {
    if (!validEditing()) {
      return;
    }

    if (country) {
      let newLocation = {
        id: 0,
        eventPlaylistId: game?.id ?? 0,
        locationCountryISO: country?.iso!,
        country: country?.country ?? '',
        isDeleted: false
      };
      setLocationsState((prev) => {
        const result = cloneDeep(prev);
        result.splice(idx, 1, newLocation);

        return result;
      });
    }
  };

  const onAddNewLocation = () => {
    const newLocation = {
      id: 0,
      eventPlaylistId: game?.id ?? 0,
      locationCountryISO: '',
      country: '',
      isDeleted: false
    };

    setLocationsState((prev) => [...prev, newLocation]);
  };

  const onChangeCity = (idx: number, city: string) => {
    if (!validEditing()) {
      return;
    }

    setLocationsState((prev) => {
      const result = cloneDeep(prev);
      result[idx].city = city;
      return result;
    });
  };

  const onChangeTimezoneHandler = (e: ComboBoxChangeEvent) => {
    if (!validEditing()) {
      return;
    }
    const newEvent = { ...game };
    const timezone = e.value;

    if (newEvent) {
      newEvent.timeZone = timezone?.id;
    }

    onChangeActivityHandler(newEvent);
  };

  const onChangeEnvironmentHandler = (e: ComboBoxChangeEvent) => {
    if (!validEditing()) {
      return;
    }
    let newEvent = { ...game };
    let env = e.value as dictionary;

    if (newEvent) {
      newEvent.environmentId = env?.id;
      if (game) {
        game.environmentId = env?.id;
      }
    }

    onChangeActivityHandler(newEvent);
  };

  const onChangeFacilitatorhandler = (user: User) => {
    if (!validEditing()) {
      return;
    }
    if (user) {
      let newFacilitator = {
        id: 0,
        firstName: user?.firstName,
        fullName: user?.fullName,
        facilitatorUserProfileId: user?.id,
        isDeleted: false
      };

      onChangeFacilitator(newFacilitator);
    }
  };

  const onChangeSelectGameHandler = (changeEvent: ComboBoxChangeEvent) => {
    if (!validEditing()) {
      return;
    }

    let newEvent = { ...game };

    if (changeEvent.value?.type === 'TILO') {
      // TILO selected
      newEvent.type = 'TILO';
      newEvent.name = changeEvent.value?.name;
      newEvent.gameId = changeEvent.value?.id;
      newEvent.isGameActivity = false;
      newEvent.isAssessmentActivity = false;
      newEvent.assessmentId = undefined;
      newEvent.tilIsCollab = undefined;
      newEvent.tilTemplateConfig = undefined;
    } else if (changeEvent.value?.type === 'TIL') {
      // TIL selected
      newEvent.type = 'TIL';
      newEvent.name = changeEvent.value?.name;
      newEvent.gameId = changeEvent.value?.id;
      newEvent.isGameActivity = false;
      newEvent.isAssessmentActivity = false;
      newEvent.assessmentId = undefined;
      newEvent.tiloNbrDoors = undefined;
      newEvent.tiloNbrPlayersPerTeam = undefined;
      newEvent.tiloPlaytime = undefined;
      newEvent.tiloGameType = undefined;
      newEvent.tilIsCollab = undefined;
      if (!newEvent.tilTemplateConfig) {
        newEvent.tilTemplateConfig = JSON.stringify(buildDefaultTilConfig());
      }
    } else if (changeEvent.value?.type === 'SPA') {
      // Spark selected
      newEvent.type = 'SPA';
      newEvent.name = changeEvent.value?.name;
      newEvent.gameId = changeEvent.value?.id;
      newEvent.isGameActivity = true;
      newEvent.isAssessmentActivity = false;
      newEvent.assessmentId = undefined;
      newEvent.tiloNbrDoors = undefined;
      newEvent.tiloTeamsCount = undefined;
      newEvent.tiloNbrPlayersPerTeam = undefined;
      newEvent.tiloPlaytime = undefined;
      newEvent.tiloGameType = undefined;
      newEvent.tilIsCollab = undefined;
      newEvent.tilTemplateConfig = undefined;
      newEvent.challenges = undefined;
    } else if (changeEvent.value) {
      // Standard game selected — clear any TILO/TIL state
      const isWstVal =
        changeEvent.value?.type === 'WST' ||
        changeEvent.value?.name === "World's Strongest Team";
      newEvent.type = isWstVal ? 'WST' : undefined;
      newEvent.name = changeEvent.value?.name;
      newEvent.gameId = changeEvent.value?.id;
      newEvent.isGameActivity = true;
      newEvent.tiloNbrDoors = undefined;
      newEvent.tiloTeamsCount = undefined;
      newEvent.tiloNbrPlayersPerTeam = undefined;
      newEvent.tiloPlaytime = undefined;
      newEvent.tiloGameType = undefined;
      newEvent.tilIsCollab = undefined;
      newEvent.tilTemplateConfig = undefined;
      if (isWstVal) {
        newEvent.challenges = challenges;
      } else {
        newEvent.challenges = undefined;
      }
    } else {
      // cleared
      newEvent.type = undefined;
      newEvent.name = undefined;
      newEvent.approxPax = undefined;
      newEvent.gameId = undefined;
      newEvent.isGameActivity = false;
      newEvent.tiloNbrDoors = undefined;
      newEvent.tiloTeamsCount = undefined;
      newEvent.tiloNbrPlayersPerTeam = undefined;
      newEvent.tiloPlaytime = undefined;
      newEvent.tiloGameType = undefined;
      newEvent.tilIsCollab = undefined;
      newEvent.tilTemplateConfig = undefined;
    }

    onChangeActivityHandler(newEvent);
    if (onChange && (index as number) > -1) {
      onChange(newEvent, index as number);
    }
  };

  const onChangeSelectAssessmentHandler = (
    changeEvent: ComboBoxChangeEvent
  ) => {
    if (!validEditing()) {
      return;
    }

    let newEvent = { ...game };
    if (changeEvent.value) {
      newEvent.isAssessmentActivity = true;
      newEvent.assessmentId = changeEvent.value?.id;
    } else {
      newEvent.isAssessmentActivity = false;
      newEvent.assessmentId = undefined;
    }

    onChangeActivityHandler(newEvent);
    if (onChange && (index as number) > -1) {
      onChange(newEvent, index as number);
    }
  };

  const [loading, setLoading] = useState<{
    game: boolean;
    assessment: boolean;
  }>({
    game: false,
    assessment: false
  });

  const filterGameData = useCallback(
    (filter: FilterDescriptor) => {
      return filterBy(libraries ?? [], filter);
    },
    [libraries]
  );

  const filterAssessmentData = useCallback(
    (filter: FilterDescriptor) => {
      return filterBy(assessments ?? [], filter);
    },
    [assessments]
  );

  // debounce 500ms so filtering doesnt take affect after every keystroke
  const timeout = useRef<any>(null);
  const filterGameChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setGameData(filterGameData(event.filter));
      setLoading((prev) => ({ ...prev, game: false }));
    }, 500);
    setLoading((prev) => ({ ...prev, game: true }));
  };

  const filterAssessmentChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setAssessmentData(filterAssessmentData(event.filter));
      setLoading((prev) => ({ ...prev, assessment: false }));
    }, 500);
    setLoading((prev) => ({ ...prev, assessment: true }));
  };

  const itemTemplate = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    let itemChildren;
    if (itemProps.dataItem.type === 'Game') {
      itemChildren = (
        <div
          style={{
            borderTop: itemProps.dataItem.isGameDivider
              ? '1px solid #aaa'
              : undefined,
            paddingTop: itemProps.dataItem.isGameDivider ? '8px' : undefined
          }}>
          {itemProps.dataItem.name}
        </div>
      );
    } else {
      itemChildren = (
        <div>
          <span>{itemProps.dataItem.name} </span>
          <span className={'text-success fw-bold'}>
            {itemProps.dataItem.type}
          </span>
        </div>
      );
    }

    return React.cloneElement(li, li.props, itemChildren);
  };

  useEffect(() => {
    if (game) {
      let newEventGame = { ...game };
      if (game.gameId && game.gameId > 0) {
        newEventGame.isGameActivity = true;
      }
      if (game.assessmentId && game.assessmentId > 0) {
        newEventGame.isAssessmentActivity = true;
      }
      if (game.type === 'TIL' && !game.tilTemplateConfig) {
        const defaultConfig = JSON.stringify(buildDefaultTilConfig());
        newEventGame.tilTemplateConfig = defaultConfig;
        onChangeField('tilTemplateConfig', defaultConfig);
      }
      setEventGame(newEventGame);
    }
  }, [game]);

  return (
    <div>
      <div>Activity {(index as number) + 1}</div>
      <div className="card p-4 d-flex flex-column gap-2">
        <Row className={'p-1'}>
          <Col md="7">
            <Label>Game name</Label>
            <ComboBox
              data={gameData}
              textField={'name'}
              itemRender={itemTemplate}
              dataItemKey={'id'}
              filterable={true}
              onFilterChange={filterGameChange}
              onChange={onChangeSelectGameHandler}
              loading={loading.game}
              allowCustom={false}
              value={{
                id: libraries?.find((x) => x.id === eventGame?.gameId)?.id,
                name: libraries?.find((x) => x.id === eventGame?.gameId)?.name
              }}
            />
          </Col>
          {eventGame.gameId &&
            eventGame.type !== 'TILO' &&
            eventGame.type !== 'TIL' &&
            eventGame.type !== 'WST' &&
            eventGame.type !== 'SPA' && (
              <Col md="5" className={'d-flex align-items-center pt-3'}>
                <span
                  className={'material-symbols-outlined'}
                  style={{ cursor: 'pointer', paddingTop: '10px' }}
                  onClick={handleChangeGameSettings}>
                  settings
                </span>
              </Col>
            )}
          {/*
            The cog/settings icon for the TIL configuration editor has been retired.
            Game and template configuration is maintained directly within the TIL Server unity app.
            Ref: https://dev.azure.com/catalyst-global/TIL%20The%20Infinite%20Loop/_workitems/edit/11620
          */}
          {isWst && (
            <>
              <Col md="3">
                <Label>Start date*</Label>
                <DatePicker
                  format="dd/MMM/yyyy"
                  value={convertJsonDateToDate(eventGame.startDateUtc)}
                  onChange={(e) => onChangeDateField('startDateUtc', e.value)}
                />
                {!game?.startDateUtc && eventGame.type !== 'TIL' && (
                  <Error>Start date is required</Error>
                )}
              </Col>
              <Col md="2">
                <Label>Start time*</Label>
                <TimePicker
                  format="HH:mm"
                  value={convertJsonDateToDate(eventGame.startDateUtc)}
                  onChange={(e) => onChangeDateField('startDateUtc', e.value)}
                />
                {!game?.startDateUtc && eventGame.type !== 'TIL' && (
                  <Error>Start time is required</Error>
                )}
              </Col>
            </>
          )}
        </Row>

        {isWst && (
          <Row className={'p-1'}>
            <Col md="7">
              <Label style={{ whiteSpace: 'nowrap' }}>Time zone</Label>
              <OptimizedComboboxInput
                data={timezones}
                textField="displayName"
                onChange={onChangeTimezoneHandler}
                value={
                  timezones?.find((x) => x.id === eventGame?.timeZone) || ''
                }
                filterable
              />
            </Col>
          </Row>
        )}

        {isWst && (
          <Row className={'p-1'}>
            <Col md="12">
              <div className="d-flex flex-column gap-1 mb-2">
                <div className="d-flex align-items-center gap-1">
                  <Label className="fw-bold m-0">Challenges*</Label>
                  <span
                    className="material-symbols-outlined text-primary fs-6 cursor-pointer"
                    title="Select the challenges that will be performed at this event. Participants will only be able to submit attempts for the selected challenges.">
                    info
                  </span>
                </div>
                <small className="text-muted">
                  Select the challenges that will be performed at this event.
                  Participants will only be able to submit attempts for the
                  selected challenges.
                </small>
                <div className="d-flex flex-wrap gap-3 mt-2 align-items-center">
                  {challenges.map((ch) => {
                    const isChecked = (eventGame.challenges ?? []).some(
                      (c) => c.id === ch.id
                    );
                    return (
                      <Checkbox
                        key={ch.id}
                        label={ch.name}
                        checked={isChecked}
                        onChange={(e) => {
                          let updated: Challenge[] = [
                            ...(eventGame.challenges ?? [])
                          ];
                          if (e.value) {
                            if (!updated.some((c) => c.id === ch.id)) {
                              updated.push(ch);
                            }
                          } else {
                            updated = updated.filter((c) => c.id !== ch.id);
                          }
                          onChangeField('challenges', updated);
                        }}
                      />
                    );
                  })}
                </div>
                <small className="text-muted">
                  (at least one challenge is required)
                </small>
                {(!eventGame.challenges ||
                  eventGame.challenges.length === 0) && (
                  <Error>At least one challenge must be selected.</Error>
                )}
              </div>
            </Col>
          </Row>
        )}

        {eventGame.type !== 'TILO' && eventGame.type !== 'TIL' && !isWst && 
          eventGame.type !== 'SPA' && (
          <>
            <Row>
              <Col md="7">
                <Checkbox
                  name={'allowGameAutomaticStart'}
                  label={'Auto-start game based on start date'}
                  size={'large'}
                  onChange={(e) =>
                    onChangeField('allowGameAutomaticStart', e.value)
                  }
                  disabled={!eventGame.isGameActivity}
                  value={game?.allowGameAutomaticStart}
                />
              </Col>
            </Row>

            <Row className={'p-1'}>
              <Col md="7">
                <Label>Assessment name</Label>
                <ComboBox
                  data={assessmentData}
                  textField={'name'}
                  dataItemKey={'id'}
                  filterable={true}
                  onFilterChange={filterAssessmentChange}
                  onChange={onChangeSelectAssessmentHandler}
                  loading={loading.assessment}
                  allowCustom={false}
                  value={{
                    id: assessmentData?.find(
                      (x) => x.id === eventGame?.assessmentId
                    )?.id,
                    name: assessmentData?.find(
                      (x) => x.id === eventGame?.assessmentId
                    )?.name
                  }}
                />
              </Col>
            </Row>
          </>
        )}

        {eventGame.type === 'TILO' && (
          <>
            <Row className={'p-1'}>
              <Col md="5">
                <Label>Number of doors*</Label>
                <div className="d-flex gap-2 mt-1">
                  {[8, 12].map((doors) => (
                    <button
                      key={doors}
                      type="button"
                      className={`btn btn-sm ${eventGame.tiloNbrDoors === doors ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => onChangeField('tiloNbrDoors', doors)}>
                      {doors}
                    </button>
                  ))}
                </div>
                <small className="text-muted d-block mt-1">
                  Cannot be changed after the game is created.
                </small>
                {!eventGame.tiloNbrDoors && (
                  <Error>Number of doors is required</Error>
                )}
              </Col>
              <Col md="7">
                <Label>Session type*</Label>
                <div className="d-flex gap-2 mt-1">
                  {(['test', 'demo', 'live'] as const).map((gt) => (
                    <button
                      key={gt}
                      type="button"
                      className={`btn btn-sm ${eventGame.tiloGameType === gt ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => onChangeField('tiloGameType', gt)}>
                      {gt.charAt(0).toUpperCase() + gt.slice(1)}
                    </button>
                  ))}
                </div>
                {!eventGame.tiloGameType && (
                  <Error>Session type is required</Error>
                )}
              </Col>
            </Row>
            <Row className={'p-1'}>
              <Col md="12">
                <div className="d-flex align-items-center gap-2 mt-1">
                  <Checkbox
                    checked={eventGame.tiloHideScore ?? false}
                    onChange={(e) => onChangeField('tiloHideScore', e.value)}
                    label="Hide score from players"
                  />
                </div>
                <small className="text-muted d-block mt-1">
                  When enabled, players cannot see the leaderboard in their game
                  client. Can be toggled during the session.
                </small>
              </Col>
            </Row>
            <Row className={'p-1'}>
              <Col md="4">
                <FormInput
                  label="Number of teams*"
                  type="number"
                  value={eventGame.tiloTeamsCount?.toString() ?? ''}
                  onChange={(e) =>
                    onChangeField(
                      'tiloTeamsCount',
                      e.value ? parseInt(e.value) : undefined
                    )
                  }
                />
                {!eventGame.tiloTeamsCount && (
                  <Error>Number of teams is required</Error>
                )}
              </Col>
              <Col md="4">
                <FormInput
                  label="Players per team*"
                  type="number"
                  value={eventGame.tiloNbrPlayersPerTeam?.toString() ?? ''}
                  onChange={(e) =>
                    onChangeField(
                      'tiloNbrPlayersPerTeam',
                      e.value ? parseInt(e.value) : undefined
                    )
                  }
                />
                {!eventGame.tiloNbrPlayersPerTeam && (
                  <Error>Players per team is required</Error>
                )}
                {!!eventGame.tiloNbrPlayersPerTeam &&
                  (() => {
                    const v = eventGame.tiloNbrPlayersPerTeam!;
                    return v < 2 || v > 8 ? (
                      <Error>Must be between 2 and 8</Error>
                    ) : null;
                  })()}
                <small className="text-muted d-block mt-1">
                  2 to 8 players per team.
                </small>
              </Col>
              <Col md="4">
                <FormInput
                  label="Duration (min)*"
                  type="number"
                  value={eventGame.tiloPlaytime?.toString() ?? ''}
                  onChange={(e) =>
                    onChangeField(
                      'tiloPlaytime',
                      e.value ? parseInt(e.value) : undefined
                    )
                  }
                />
                {!eventGame.tiloPlaytime && <Error>Duration is required</Error>}
                {!!eventGame.tiloPlaytime &&
                  (() => {
                    const v = eventGame.tiloPlaytime!;
                    return v < 20 || v > 180 ? (
                      <Error>Must be between 20 and 180 minutes</Error>
                    ) : null;
                  })()}
                <small className="text-muted d-block mt-1">
                  20 to 180 minutes.
                </small>
              </Col>
            </Row>
          </>
        )}

        {eventGame.type === 'TIL' && (
          <>
            <Row className={'p-1'}>
              <Col md="4">
                <FormInput
                  label="Number of teams*"
                  type="number"
                  value={eventGame.tiloTeamsCount?.toString() ?? ''}
                  onChange={(e) =>
                    onChangeField(
                      'tiloTeamsCount',
                      e.value ? parseInt(e.value) : undefined
                    )
                  }
                />
                {!eventGame.tiloTeamsCount && (
                  <Error>Number of teams is required</Error>
                )}
                {!!eventGame.tiloTeamsCount &&
                  eventGame.tilIsCollab &&
                  (() => {
                    const v = eventGame.tiloTeamsCount!;
                    return v < 2 || v > 24 ? (
                      <Error>
                        Must be between 2 and 24 for collaborative sessions
                      </Error>
                    ) : null;
                  })()}
              </Col>
              <Col md="4">
                <FormInput
                  label="Duration (min)*"
                  type="number"
                  value={eventGame.tiloPlaytime?.toString() ?? ''}
                  onChange={(e) =>
                    onChangeField(
                      'tiloPlaytime',
                      e.value ? parseInt(e.value) : undefined
                    )
                  }
                />
                {!eventGame.tiloPlaytime && <Error>Duration is required</Error>}
                {!!eventGame.tiloPlaytime && eventGame.tiloPlaytime <= 0 && (
                  <Error>Must be greater than 0</Error>
                )}
              </Col>
            </Row>
            <Row className={'p-1'}>
              <Col md="6">
                <Label>Game mode*</Label>
                <div className="d-flex gap-2 mt-1">
                  {(['Collaborative', 'Competitive'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn btn-sm ${
                        mode === 'Collaborative'
                          ? eventGame.tilIsCollab === true
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                          : eventGame.tilIsCollab === false
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                      }`}
                      onClick={() =>
                        onChangeField('tilIsCollab', mode === 'Collaborative')
                      }>
                      {mode}
                    </button>
                  ))}
                </div>
                {eventGame.tilIsCollab === undefined && (
                  <Error>Game mode is required</Error>
                )}
              </Col>
              <Col md="6">
                <Label>Session type*</Label>
                <div className="d-flex gap-2 mt-1">
                  {(['live', 'test'] as const).map((gt) => (
                    <button
                      key={gt}
                      type="button"
                      className={`btn btn-sm ${eventGame.tiloGameType === gt ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => onChangeField('tiloGameType', gt)}>
                      {gt.charAt(0).toUpperCase() + gt.slice(1)}
                    </button>
                  ))}
                </div>
                {!eventGame.tiloGameType && (
                  <Error>Session type is required</Error>
                )}
              </Col>
            </Row>
          </>
        )}

        {eventGame.type !== 'TILO' &&
          eventGame.type !== 'TIL' &&
          eventGame.type !== 'WST' &&
          eventGame.type !== 'SPA' && (
            <Row className="p-1">
              <Col md="12">
                <div
                  className="p-4"
                  style={{
                    backgroundColor: '#f7fcff',
                    textAlign: 'justify'
                  }}>
                    <span className="fw-bold text-primary">
                      Environment and Duration
                    </span>
                    <div className="text-muted small mt-1">
                    Accuracy is important. This information instructs the Server
                    Environment and informs the support team of your event.
                    Failure to add accurate information could jeapodise your
                    event. You can edit this information before the activity
                    starts.
                    </div>
                </div>
              </Col>
            </Row>
          )}

        {eventGame.type !== 'TIL' && !isWst && 
          eventGame.type !== 'SPA' && (
          <Row className={'p-1'}>
            <Col md="7">
              <Label>Environment*</Label>
              <ComboboxInput
                data={environments}
                textField="name"
                filterable={true}
                onChange={onChangeEnvironmentHandler}
                value={
                  environments?.find(
                    (x) => x.id === eventGame?.environmentId
                  ) || ''
                }
              />
              {eventGame?.environmentId === undefined && (
                <Error>{'Environment is required'}</Error>
              )}
            </Col>
            <Col md="5">
              <Label style={{ whiteSpace: 'nowrap' }}>Time zone</Label>
              <OptimizedComboboxInput
                data={timezones}
                textField="displayName"
                onChange={onChangeTimezoneHandler}
                value={
                  timezones?.find((x) => x.id === eventGame?.timeZone) || ''
                }
                filterable
              />
            </Col>
          </Row>
        )}

        {!isWst && (
          <Row className={'p-1'}>
            <Col md="4">
              <Label>Start date</Label>
              <DatePicker
                format="dd/MMM/yyyy"
                value={convertJsonDateToDate(eventGame.startDateUtc)}
                onChange={(e) => onChangeDateField('startDateUtc', e.value)}
              />
              {!game?.startDateUtc && eventGame.type !== 'TIL' && (
                <Error>Start date is required</Error>
              )}
            </Col>
            <Col md="3">
              <Label>Time</Label>
              <TimePicker
                value={convertJsonDateToDate(eventGame.startDateUtc)}
                onChange={(e) => onChangeDateField('startDateUtc', e.value)}
              />
              {!game?.startDateUtc && eventGame.type !== 'TIL' && (
                <Error>Time is required</Error>
              )}
            </Col>
          </Row>
        )}

        {!isWst && (
          <Row className={'d-flex p-1'}>
            <Col md="4">
              <Label>End date</Label>
              <DatePicker
                format="dd/MMM/yyyy"
                value={convertJsonDateToDate(eventGame.endDateUtc)}
                onChange={(e) => onChangeDateField('endDateUtc', e.value)}
              />
              {!game?.endDateUtc && eventGame.type !== 'TIL' && (
                <Error>End date is required</Error>
              )}
            </Col>
            <Col md="3">
              <Label>Time</Label>
              <TimePicker
                value={convertJsonDateToDate(eventGame.endDateUtc)}
                onChange={(e) => onChangeDateField('endDateUtc', e.value)}
              />
              {!game?.endDateUtc && eventGame.type !== 'TIL' && (
                <Error>Time is required</Error>
              )}
            </Col>
          </Row>
        )}

        {eventGame.type !== 'TIL' && !isWst && 
          eventGame.type !== 'SPA' && (
          <Row className="p-1">
            <Col md="12">
              <div
                className="p-4"
                style={{ backgroundColor: '#f7fcff', textAlign: 'justify' }}>
                <span className="fw-bold text-primary">
                  Important
                </span>
                <div className="text-muted small mt-1"> 
                  Your activity will automatically end 48 hours after the end date
                  selected.
                </div>
              </div>
            </Col>
          </Row>
        )}

        <Row>
          <Col md="7">
            <Label>Language{eventGame.type !== 'TIL' ? '*' : ''}</Label>
            <OptimizedComboboxInput
              key={
                eventGame.type === 'TILO'
                  ? `tilo-${eventGame.tiloNbrDoors ?? 'all'}`
                  : 'standard'
              }
              data={eventGame.type === 'TILO' ? tiloLanguages : languages}
              onChange={(e) => onChangeField('language', e.value ?? '')}
              value={eventGame.language}
              filterable
            />
            {!game?.language && eventGame.type !== 'TIL' && (
              <Error>Language is required</Error>
            )}
          </Col>
          <Col md="5">
            <FormInput
              label={`Pax${eventGame.type !== 'TIL' ? '*' : ''}`}
              value={game?.approxPax ?? ''}
              onChange={(e) => onChangeField('approxPax', e.value)}></FormInput>
            {!game?.approxPax && eventGame.type !== 'TIL' && (
              <Error>Pax is required</Error>
            )}
          </Col>
        </Row>

        {eventGame.type === 'TIL' && (
          <Row className="p-1">
            <Col md="12">
              <div className="p-4" style={{ backgroundColor: '#f7fcff' }}>
                <span className="fw-bold text-primary">
                  TIL — The Infinite Loop
                </span>
                <div className="text-muted small mt-1"> 
                  This game runs on a local instance. Once saved, the local TIL
                  server will pick up this event and create the game
                  automatically.
                </div>
              </div>
            </Col>
          </Row>
        )}

        {eventGame.type === 'SPA' && (
          <Row className={'p-2'}>
            <Col md="12">
              {!eventGame.sparkGameSetupUrl ? (
                <div className="alert alert-warning small mb-0 py-2">
                  <p className="mb-2">
                    <b>To configure your Spark game</b>, first save this event. You’ll be returned to <b>My Events</b>. Find the event and click <b>Edit</b><span className="material-symbols-outlined ms-1 fs-3">edit</span>. Then click the <b>Open Spark Editor</b> button to configure your game. You can configure your <b>Spark game</b> right up until you start the game.
                  </p>
                </div>
              ) : (
                <div
                  className="p-4 d-flex flex-column gap-3 rounded"
                  style={{ backgroundColor: '#f7fcff', border: '1px solid #d0ebff' }}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <span className="fw-bold text-primary">
                        Spark Game Activity
                      </span>
                    </div>

                    {eventGame.sparkGameStatus && (
                      <span
                        className={`${
                          eventGame.sparkGameStatus.toLowerCase() === 'ready'
                            ? 'bg-success'
                            : eventGame.sparkGameStatus.toLowerCase() === 'locked'
                            ? 'bg-primary'
                            : eventGame.sparkGameStatus.toLowerCase() === 'completed'
                            ? 'bg-secondary'
                            : 'bg-warning'
                        }`}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '5px',
                          fontSize: '14px',
                          color:
                            eventGame.sparkGameStatus.toLowerCase() === 'ready' ||
                            eventGame.sparkGameStatus.toLowerCase() === 'locked' ||
                            eventGame.sparkGameStatus.toLowerCase() === 'completed'
                              ? 'white'
                              : '#212529'
                        }}
                      >
                        {eventGame.sparkGameStatus.toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Spark Editor Deep Link & Guidance in Edit Mode */}
                  {(!eventGame.sparkGameStatus ||
                    eventGame.sparkGameStatus.toLowerCase() === 'draft' ||
                    eventGame.sparkGameStatus.toLowerCase() === 'ready') && (
                    <div className="d-flex flex-column gap-3 bg-white p-3 rounded border">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                          <div className="fw-bold">Configure your Spark Game</div>
                          <small className="text-muted">
                            Open the Spark Editor to configure your game.
                          </small>
                        </div>
                        <Button
                          themeColor={'primary'}
                          onClick={() =>
                            window.open(
                              eventGame.sparkGameSetupUrl,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          Open Spark Editor ↗
                        </Button>
                      </div>

                      <div className="small text-muted border-top pt-2" style={{ lineHeight: '1.5' }}>
                        <p className="mb-2">
                          <strong>Player and Facilitator access:</strong> Once you’ve configured your game, mark it as Ready in Spark. Your Player and Facilitator links will then be available here.
                        </p>
                        <p className="mb-0">
                          <strong>Note:</strong> You can continue configuring your Spark game after it has been marked Ready, right up until you start the game.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Facilitator and Player Links (Available when READY or LOCKED) */}
                  {(eventGame.sparkGameStatus?.toLowerCase() === 'ready' ||
                    eventGame.sparkGameStatus?.toLowerCase() === 'locked') && (
                    <div className="d-flex flex-column gap-3 bg-white p-3 rounded border">
                      {eventGame.sparkJoinCode && (
                        <div>
                          <small className="text-muted d-block">Join Code</small>
                          <span className="fw-bold fs-6">{eventGame.sparkJoinCode}</span>
                        </div>
                      )}

                      {eventGame.sparkFacilitatorUrl && (
                        <div className="d-flex flex-column">
                          <small className="text-muted">
                            Spark - Facilitator Screen (Projector) + QR Code
                          </small>
                          <QRLink
                            title={'Spark - Facilitator Link'}
                            activityName={eventGame.name || 'Spark Game'}
                            url={eventGame.sparkFacilitatorUrl}
                          />
                        </div>
                      )}

                      {eventGame.sparkJoinUrl && (
                        <div className="d-flex flex-column">
                          <small className="text-muted">
                            Spark - Participant Join Link + QR Code
                          </small>
                          <QRLink
                            title={`Spark - Player Join (Code: ${eventGame.sparkJoinCode ?? ''})`}
                            activityName={eventGame.name || 'Spark Game'}
                            url={eventGame.sparkJoinUrl}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {(eventGame.sparkGameStatus?.toLowerCase() === 'locked' ||
                    eventGame.sparkGameStatus?.toLowerCase() === 'completed') && (
                    <div className="small text-muted">
                      {eventGame.sparkGameStatus?.toLowerCase() === 'locked'
                        ? 'This game is currently LOCKED (session in progress).'
                        : 'This game session is COMPLETED.'}
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        )}

        <>
          <Row>
            <Label className={'mt-2'}>Location(s)*</Label>
            <Col md={'12'} className={'px-4'}>
              {locationsState?.map((location, idx) => (
                <div
                  key={idx}
                  className={`d-flex align-items-center gap-2 ${idx !== 0 && 'mt-2'}`}>
                  <div className={'d-flex align-items-start gap-2 w-100'}>
                    <div className={'d-flex flex-column w-50'}>
                      <Label>Country</Label>
                      <Autocomplete<CountryResponse>
                        className={'d-flex justify-content-start w-100'}
                        label={'Location/s'}
                        items={countries ?? []}
                        textField={'country'}
                        onChange={(country) =>
                          onChangeLocationHandler(idx, country)
                        }
                        onClear={() => {
                          const result = cloneDeep(location);
                          result.country = '';
                          result.locationCountryISO = '';
                          onChangeLocationHandler(idx, result);
                        }}
                        value={location}
                      />
                      {!location.country && <Error>Country is required</Error>}
                    </div>

                    <div className={'d-flex flex-column w-50'}>
                      <FormInput
                        label={'City'}
                        value={location.city}
                        onChange={(e) => {
                          onChangeCity(idx, (e.target.value as string) ?? '');
                        }}
                      />
                      {!location.city && <Error>City is required</Error>}
                    </div>
                  </div>

                  <div className={'d-flex flex-column'}>
                    <Label />
                    <span
                      className={'material-symbols-outlined text-danger ml-3'}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        onDeleteLocation(
                          location,
                          locationsState.length > 1 ? 'delete' : 'reset'
                        )
                      }>
                      delete
                    </span>
                    {(!location.country || !location.city) && (
                      <Error
                        style={{
                          color: 'transparent'
                        }}>
                        Error
                      </Error>
                    )}
                  </div>
                </div>
              ))}
            </Col>

            {eventGame.type !== 'TILO' && !isWst && (
              <Col md="12" className="mt-2 px-4">
                <Button
                  className="w-full"
                  svgIcon={plusIcon}
                  onClick={onAddNewLocation}>
                  Add location
                </Button>
              </Col>
            )}
          </Row>

          <Row className={'d-flex p-1'}>
            <Col md="12">
              <Label>Facilitator/s</Label>
            </Col>
            <Col md="12">
              <Autocomplete<User>
                className={'d-flex justify-content-start w-100'}
                label={'Facilitator/s'}
                items={users?.filter((user) => user.fullName !== null) ?? []}
                textField="fullName"
                onChange={onChangeFacilitatorhandler}></Autocomplete>
            </Col>
            <Col md="12" className={'mt-2'}>
              <ul className="list-group list-group-flush">
                {facilitatorsState &&
                  facilitatorsState
                    .filter((x) => !x.isDeleted)
                    .map((fc) => {
                      return (
                        <li className="list-group-item d-flex justify-content-between">
                          <span className={'text-primary'}>
                            {fc?.fullName ?? ''}
                          </span>
                          <span
                            className={'material-symbols-outlined text-danger'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              onDeleteFacilitator(fc);
                            }}>
                            close
                          </span>
                        </li>
                      );
                    })}
              </ul>
            </Col>
          </Row>

          <Row className={'p-1'}>
            <Col md="12">
              <Label>Activity notes</Label>
              <TextArea
                rows={4}
                maxLength={1000}
                value={game?.notes}
                onChange={(e) => onChangeField('notes', e.value)}
              />
              <div className="d-flex justify-content-end mt-1">
                <span className="text-muted fs-8">
                  {game?.notes?.length ?? 0} / 1000
                </span>
              </div>
            </Col>
          </Row>
        </>

        <Row className={'p-1 mt-3'}>
          <Col md="6" className={'d-flex align-items-center mt-3'}></Col>
          <Col md="6" className={'d-flex justify-content-end mt-3'}>
            <span
              className={'material-symbols-outlined'}
              style={{ cursor: 'pointer' }}
              onClick={handleCopyGame}>
              content_copy
            </span>
            <span
              className={'material-symbols-outlined text-danger'}
              style={{ cursor: 'pointer' }}
              onClick={handleDeleteGame}>
              delete
            </span>
          </Col>
        </Row>
      </div>

      {showDeleteConfirm && (
        <YesNoDialog
          title={'Remove Activity'}
          onConfirm={onConfirmDelete}
          onClose={() => setShowDeleteConfirm(false)}>
          Are you sure you want to delete this activity?
        </YesNoDialog>
      )}

      {/* 
        TilSettingsDialog is retired from the active TIL workflow.
        Ref: https://dev.azure.com/catalyst-global/TIL%20The%20Infinite%20Loop/_workitems/edit/11620
      */}
    </div>
  );
};
